import { useState } from 'react';
import { useForm, type FieldErrors, Controller } from 'react-hook-form';

import { FormProgressStepper } from '~widgets/application-form/form-progress-stepper';

import { analytics } from '~entities/amplitude-analytics';

import { useFormInitialize, useOnMountEffect } from '~shared/hooks';
import { getFieldsErrors } from '~shared/lib/get-fields-errors';
import { Button } from '~shared/ui/button';
import { FormFooter } from '~shared/ui/form-footer';
import { HeaderWithLogo } from '~shared/ui/header-with-logo';
import { Screen } from '~shared/ui/screen';
import { ScreenTitle } from '~shared/ui/screen-title';

import { RadioGroup } from '@breeze/rhf-adapters';

import { REQUIRED_TYPE_ERROR } from '../constants/field-errors';
import type {
  MainIdDocumentTypeValue,
  OtherIdDocumentTypeValue,
} from '../lib/id-document-type-options';
import {
  idDocumentTypeMainOptions,
  idDocumentTypeOtherOptions,
} from '../lib/id-document-type-options';

export type FormValues = {
  typeMain?: MainIdDocumentTypeValue;
  typeOther?: OtherIdDocumentTypeValue;
};

type Props = {
  onSubmit: (formValues: FormValues) => void;
  onFieldCompleted?: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  onPrev: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  initialValue?: Partial<FormValues>;
  applicationId?: string;
  orderId?: string;
};

export const IdDocumentType: React.FC<Props> = ({
  initialValue,
  onFieldCompleted,
  onPrev,
  onSubmit,
  applicationId,
  orderId,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: initialValue,
    mode: 'all',
  });
  const { control, getValues, handleSubmit, resetField, watch } = methods;
  const [showOtherTypes, setShowOtherTypes] = useState<boolean>(
    () => !!initialValue?.typeOther
  );
  const typeMain = watch('typeMain');

  useFormInitialize(methods, initialValue);

  useOnMountEffect(() => {
    if (applicationId) {
      analytics.track.ponApplicationKycIdSelectionView({
        application_id: applicationId,
        order_id: orderId,
      });
    }
  });

  const handleBlur = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onFieldCompleted?.(getValues(), actualErrors);
  };

  const handleClickButtonPrev = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onPrev(getValues(), actualErrors);
  };

  const handleFormSubmit = (value: FormValues) => {
    onSubmit(value);

    if (!applicationId) return;
    if (value.typeMain) {
      analytics.track.ponApplicationKycIdSelectionNextClick({
        application_id: applicationId,
        order_id: orderId,
      });
      return;
    }
    analytics.track.ponApplicationKycIdSelectionSecondaryNextClick({
      application_id: applicationId,
      order_id: orderId,
    });
  };

  const handleShowOtherDocsClick = () => {
    resetField('typeMain', { defaultValue: null });
    setShowOtherTypes(true);
    if (applicationId) {
      analytics.track.ponApplicationKycNoPrimaryIdClick({
        application_id: applicationId,
        order_id: orderId,
      });
    }
  };

  return (
    <form onBlur={handleBlur} onSubmit={handleSubmit(handleFormSubmit)}>
      <Screen
        header={<HeaderWithLogo />}
        footer={<FormFooter onClickPrev={handleClickButtonPrev} />}
      >
        <div className="mb-2 flex flex-col gap-5">
          <ScreenTitle
            title="Main identity document"
            icon={<FormProgressStepper step="identity" />}
          />
          <p className="text-xl font-bold">
            These IDs increase approval rate by&nbsp;7%
          </p>
        </div>

        <Controller
          control={control}
          name="typeMain"
          rules={{
            required: showOtherTypes ? undefined : REQUIRED_TYPE_ERROR,
          }}
          render={(fieldProps) => (
            <RadioGroup
              {...fieldProps}
              field={{
                ...fieldProps.field,
                onChange: (event, params) => {
                  methods.resetField('typeOther', { defaultValue: null });
                  fieldProps.field.onChange(event, params);
                },
              }}
              qaLabel="mainIdType"
              options={idDocumentTypeMainOptions}
              vertical
              wide
            />
          )}
        />
        {!showOtherTypes && (
          <Button
            theme="outline"
            size="l"
            onClick={handleShowOtherDocsClick}
            wide
          >
            I don&apos;t have these IDs
          </Button>
        )}
        {showOtherTypes && (
          <>
            <p className="mb-2 text-xl font-bold">Other IDs</p>
            <Controller
              control={control}
              name="typeOther"
              rules={{
                validate: (typeOther) =>
                  showOtherTypes && !typeOther && !typeMain
                    ? REQUIRED_TYPE_ERROR
                    : undefined,
              }}
              render={(fieldProps) => (
                <RadioGroup
                  {...fieldProps}
                  field={{
                    ...fieldProps.field,
                    onChange: (event, params) => {
                      methods.resetField('typeMain', { defaultValue: null });
                      fieldProps.field.onChange(event, params);
                    },
                  }}
                  qaLabel="otherIdType"
                  options={idDocumentTypeOtherOptions}
                  vertical
                  wide
                />
              )}
            />
          </>
        )}
      </Screen>
    </form>
  );
};
